import { Grid } from '@mui/material';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useBedrijfMutaties } from '../../api/klanten.ts';
import type { Bedrijf, BedrijfDetails, Bedrijfsrelatie, Locatie } from '../../api/typen.ts';
import { FormulierDialoog } from '../../componenten/FormulierDialoog.tsx';
import { LocatieVeld } from '../../componenten/keuzevelden.tsx';
import { Keuzeveld, Tekstveld } from '../../componenten/velden.tsx';
import { leegNaarNull } from '../../helpers/formulier.ts';
import { bedrijfsrelatieLabels, opties } from '../../helpers/labels.ts';
import { useVerzenden } from '../../hooks/useVerzenden.ts';

interface Waarden {
  naam: string;
  kvkNummer: string;
  sector: string;
  website: string;
  telefoon: string;
  locatie: Locatie | null;
  relatie: Bedrijfsrelatie;
}

interface Props {
  open: boolean;
  onSluit: () => void;
  bestaand?: BedrijfDetails | undefined;
  onOpgeslagen?: ((bedrijf: Bedrijf) => void) | undefined;
}

const relatieOpties = opties(bedrijfsrelatieLabels);

function beginwaarden(bestaand: BedrijfDetails | undefined): Waarden {
  return {
    naam: bestaand?.naam ?? '',
    kvkNummer: bestaand?.kvkNummer ?? '',
    sector: bestaand?.sector ?? '',
    website: bestaand?.website ?? '',
    telefoon: bestaand?.telefoon ?? '',
    locatie: bestaand?.locatie ?? null,
    relatie: bestaand?.relatie ?? 'prospect',
  };
}

/**
 * Legt een bedrijf vast of wijzigt het.
 * @param props Het bestaande bedrijf om te wijzigen, of niets voor een nieuw bedrijf.
 * @returns De dialoog.
 */
export function BedrijfFormulier({ open, onSluit, bestaand, onOpgeslagen }: Props) {
  const mutaties = useBedrijfMutaties();
  const formulier = useForm<Waarden>({ defaultValues: beginwaarden(bestaand) });
  useEffect(() => {
    if (open) {
      formulier.reset(beginwaarden(bestaand));
    }
  }, [open, bestaand, formulier]);

  const { onVerzend, fout, bezig } = useVerzenden(
    formulier,
    async (waarden) => {
      const invoer = {
        naam: waarden.naam,
        kvkNummer: leegNaarNull(waarden.kvkNummer),
        sector: leegNaarNull(waarden.sector),
        website: leegNaarNull(waarden.website),
        telefoon: leegNaarNull(waarden.telefoon),
        locatieId: waarden.locatie?.id ?? null,
        relatie: waarden.relatie,
      };
      const bedrijf = bestaand
        ? await mutaties.wijzig.mutateAsync({ id: bestaand.id, invoer })
        : await mutaties.maak.mutateAsync(invoer);
      onOpgeslagen?.(bedrijf);
    },
    { melding: bestaand ? 'Bedrijf gewijzigd' : 'Bedrijf vastgelegd', naSucces: onSluit },
  );

  return (
    <FormProvider {...formulier}>
      <FormulierDialoog
        open={open}
        titel={bestaand ? 'Bedrijf wijzigen' : 'Nieuw bedrijf'}
        onSluit={onSluit}
        onVerzend={onVerzend}
        fout={fout}
        bezig={bezig}
      >
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 8 }}>
            <Tekstveld naam="naam" label="Naam" verplicht autoFocus />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Keuzeveld naam="relatie" label="Relatie" opties={relatieOpties} verplicht />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Tekstveld naam="kvkNummer" label="KvK-nummer" placeholder="12345678" />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Tekstveld naam="sector" label="Sector" />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Tekstveld naam="website" label="Website" placeholder="voorbeeld.nl" />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Tekstveld naam="telefoon" label="Telefoon" type="tel" />
          </Grid>
          <Grid size={12}>
            <LocatieVeld naam="locatie" label="Vestigingsplaats" />
          </Grid>
        </Grid>
      </FormulierDialoog>
    </FormProvider>
  );
}
